import React, {useState} from "react";
import Tooltip from "@material-ui/core/Tooltip";
import ClickAwayListener from "@material-ui/core/ClickAwayListener";
import {makeStyles} from "@material-ui/core/styles";
import * as colors from "material-ui/styles/colors";

import {ContextNote} from "common/types/issue";

interface ContextType {
  abbreviation: string;
  name: string;
  color: string;
  background: string;
}

const CONTEXT_TYPES: Record<string, ContextType> = {
  Project: {
    abbreviation: "RC",
    name: "Regional Context",
    color: colors.blue800,
    background: colors.blue50,
  },
  Client: {
    abbreviation: "CC",
    name: "Client Context",
    color: colors.deepPurple700,
    background: colors.deepPurple50,
  },
  Deal: {
    abbreviation: "DC",
    name: "Deal Context",
    color: colors.teal800,
    background: colors.teal50,
  },
};

const FALLBACK_TYPE: ContextType = {
  abbreviation: "C",
  name: "Context",
  color: colors.grey800,
  background: colors.grey200,
};

const useStyles = makeStyles({
  tooltip: {
    maxWidth: 420,
    padding: "10px 14px",
    backgroundColor: "white",
    color: colors.grey800,
    fontSize: 13,
    lineHeight: 1.5,
    fontWeight: 400,
    border: `1px solid ${colors.grey300}`,
    borderRadius: 4,
    boxShadow: "0 4px 16px rgba(0, 0, 0, 0.15)",
  },
  arrow: {
    color: "white",
    "&::before": {
      border: `1px solid ${colors.grey300}`,
    },
  },
  name: {
    fontWeight: 600,
    fontSize: 11,
    textTransform: "uppercase",
    letterSpacing: "0.04em",
    marginBottom: 6,
  },
});

function chipStyle(type: ContextType): React.CSSProperties {
  return {
    display: "inline-flex",
    alignItems: "center",
    height: 16,
    padding: "0 5px",
    margin: "0 0 0 3px",
    borderRadius: 8,
    fontSize: 10,
    fontWeight: 600,
    lineHeight: 1,
    verticalAlign: "text-bottom",
    color: type.color,
    background: type.background,
    border: `1px solid ${type.color}33`,
    cursor: "pointer",
    userSelect: "none",
  };
}

interface ContextChipProps {
  note: ContextNote;
}

function ContextChip({note}: ContextChipProps) {
  const classes = useStyles();
  const [isPinned, setIsPinned] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const type = CONTEXT_TYPES[note.section] ?? FALLBACK_TYPE;

  const title = (
    <div>
      <div className={classes.name} style={{color: type.color}}>
        {type.name}
      </div>
      <div>{note.text}</div>
    </div>
  );

  const toggle = () => setIsPinned(!isPinned);

  return (
    <ClickAwayListener onClickAway={() => setIsPinned(false)}>
      <Tooltip
        title={title}
        open={isPinned || isHovered}
        onOpen={() => setIsHovered(true)}
        onClose={() => setIsHovered(false)}
        interactive
        arrow
        classes={{tooltip: classes.tooltip, arrow: classes.arrow}}
      >
        <span
          role="button"
          tabIndex={0}
          aria-label={`${type.name}: ${note.text}`}
          style={chipStyle(type)}
          onClick={toggle}
          onKeyDown={event => {
            if (event.key === "Enter" || event.key === " ") {
              event.preventDefault();
              toggle();
            }
          }}
        >
          {type.abbreviation}
        </span>
      </Tooltip>
    </ClickAwayListener>
  );
}

export default ContextChip;
